import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Calendar,
  UsersRound,
  BarChart3,
  FileText,
  Settings as SettingsIcon,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  Building2,
  Sparkles,
  ShieldCheck,
  Activity,
} from 'lucide-react';

import { useDemoMode } from '../../contexts/DemoModeContext.jsx';
import { useRole } from '../../contexts/RoleContext.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { useCredits } from '../../hooks/useCredits.js';

// Role-aware navigation as defined in CLAUDE.md:
//   team_leader: sees everything
//   agent:      Briefing, Leads only
//   isa:        Briefing, Leads, Follow-ups
const NAV = [
  {
    to: '/dashboard',
    label: 'Briefing',
    icon: LayoutDashboard,
    roles: ['team_leader', 'agent', 'isa'],
  },
  {
    to: '/leads',
    label: 'Leads',
    icon: Users,
    roles: ['team_leader', 'agent', 'isa'],
  },
  {
    to: '/conversations',
    label: 'Conversations',
    icon: MessageSquare,
    roles: ['team_leader'],
  },
  {
    to: '/showings',
    label: 'Showings',
    icon: Calendar,
    roles: ['team_leader'],
  },
  {
    to: '/followups',
    label: 'Follow-ups',
    icon: ClipboardCheck,
    roles: ['isa', 'team_leader'],
  },
  {
    to: '/enrich',
    label: 'Live Demo',
    icon: Sparkles,
    roles: ['team_leader'],
  },
  { to: '/team', label: 'Team', icon: UsersRound, roles: ['team_leader'] },
  {
    to: '/analytics',
    label: 'Analytics',
    icon: BarChart3,
    roles: ['team_leader'],
  },
  { to: '/reports', label: 'Reports', icon: FileText, roles: ['team_leader'] },
  {
    to: '/settings',
    label: 'Settings',
    icon: SettingsIcon,
    roles: ['team_leader'],
  },
];

// Admin-only navigation. Rendered as its own section below the main nav so
// Fabio's internal tooling is visually separated from the customer-facing
// product. Only visible to users whose role is super_admin.
const ADMIN_NAV = [
  {
    to: '/admin/clients',
    label: 'Clients',
    icon: Building2,
  },
  {
    to: '/admin/usage',
    label: 'Usage',
    icon: Activity,
  },
];

const ROLE_PILLS = [
  { value: 'team_leader', short: 'Leader', tiny: 'L' },
  { value: 'agent', short: 'Agent', tiny: 'A' },
  { value: 'isa', short: 'ISA', tiny: 'I' },
];

const DIVIDER = 'rgba(255,255,255,0.08)';

function creditColor(ratio, remaining) {
  if (remaining <= 0) return '#ef4444'; // red
  if (ratio <= 0.2) return '#f59e0b'; // amber — 20% or less remaining
  return '#EA580C'; // accent orange
}

function CreditCounter({ collapsed }) {
  const credits = useCredits();
  if (credits.loading || credits.total <= 0) return null;

  const { used, total, remaining } = credits;
  const ratio = total > 0 ? Math.max(0, Math.min(1, remaining / total)) : 0;
  const usedRatio = total > 0 ? Math.min(1, used / total) : 0;
  const color = creditColor(ratio, remaining);

  if (collapsed) {
    return (
      <div
        className="px-2 py-2"
        style={{ borderTop: `1px solid ${DIVIDER}` }}
        title={`${used.toLocaleString()} / ${total.toLocaleString()} credits used`}
      >
        <div className="h-1 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${usedRatio * 100}%`,
              backgroundColor: color,
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="px-3 pt-3 pb-2"
      style={{ borderTop: `1px solid ${DIVIDER}` }}
    >
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-sidebar-text mb-1.5">
        <span>Enrichment Credits</span>
        <span className="tabular-nums font-medium" style={{ color }}>
          {used.toLocaleString()} / {total.toLocaleString()}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${usedRatio * 100}%`,
            backgroundColor: color,
          }}
        />
      </div>
      <p className="mt-1 text-[11px]" style={{ color }}>
        {remaining <= 0
          ? 'Out of credits'
          : `${remaining.toLocaleString()} remaining`}
      </p>
    </div>
  );
}

function RoleSwitcher({ collapsed }) {
  const { role, setRole } = useRole();

  if (collapsed) {
    return (
      <div
        className="px-2 pt-3 pb-2"
        style={{ borderTop: `1px solid ${DIVIDER}` }}
      >
        <div className="flex flex-col items-center gap-1">
          {ROLE_PILLS.map((r) => {
            const active = role === r.value;
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => setRole(r.value)}
                aria-label={`Switch to ${r.short}`}
                title={`Demo role: ${r.short}`}
                className={`h-7 w-7 rounded-btn text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-accent text-white'
                    : 'text-sidebar-text hover:text-white hover:bg-white/10'
                }`}
              >
                {r.tiny}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className="px-3 pt-3 pb-2"
      style={{ borderTop: `1px solid ${DIVIDER}` }}
    >
      <div className="text-[10px] uppercase tracking-wider text-sidebar-text mb-1.5">
        Demo Role
      </div>
      <div className="grid grid-cols-3 gap-1">
        {ROLE_PILLS.map((r) => {
          const active = role === r.value;
          return (
            <button
              key={r.value}
              type="button"
              onClick={() => setRole(r.value)}
              className={`rounded-btn text-xs font-medium py-1.5 transition-colors ${
                active
                  ? 'bg-accent text-white'
                  : 'text-sidebar-text hover:text-white hover:bg-white/10'
              }`}
            >
              {r.short}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle, onHoverChange }) {
  const isDemo = useDemoMode();
  const { user } = useAuth();
  const items = NAV.filter((item) => item.roles.includes(user.role));

  return (
    <aside
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      className={`${
        collapsed ? 'w-14' : 'w-[230px]'
      } shrink-0 bg-sidebar-bg flex flex-col transition-[width] duration-200 ease-out`}
    >
      <nav className="flex-1 py-3 px-2 space-y-1">
        {items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={`${to}-${label}`}
            to={to}
            end={to === '/dashboard'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-2.5 py-2 rounded-[4px] text-[16px] font-bold transition-colors text-white ${
                isActive
                  ? 'bg-[rgba(234,88,12,0.15)]'
                  : 'hover:bg-white/5'
              }`
            }
            title={collapsed ? label : undefined}
            aria-label={label}
          >
            {({ isActive }) => (
              <>
                <Icon
                  size={21}
                  className="shrink-0"
                  color={isActive ? '#EA580C' : '#6b7280'}
                  strokeWidth={2}
                />
                {!collapsed && <span>{label}</span>}
              </>
            )}
          </NavLink>
        ))}

        {user.role === 'super_admin' && (
          <div
            className="pt-3 mt-3 space-y-1"
            style={{ borderTop: `1px solid ${DIVIDER}` }}
          >
            {!collapsed && (
              <div className="flex items-center gap-1.5 px-2.5 pb-1 text-[10px] uppercase tracking-wider text-sidebar-text">
                <ShieldCheck size={12} color="#EA580C" />
                <span>Admin</span>
              </div>
            )}
            {ADMIN_NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={`${to}-${label}`}
                to={to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-2.5 py-2 rounded-[4px] text-[16px] font-bold transition-colors text-white ${
                    isActive
                      ? 'bg-[rgba(234,88,12,0.15)]'
                      : 'hover:bg-white/5'
                  }`
                }
                title={collapsed ? `Admin · ${label}` : undefined}
                aria-label={`Admin ${label}`}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={21}
                      className="shrink-0"
                      color={isActive ? '#EA580C' : '#6b7280'}
                      strokeWidth={2}
                    />
                    {!collapsed && <span>{label}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      {isDemo && <RoleSwitcher collapsed={collapsed} />}

      <CreditCounter collapsed={collapsed} />

      <button
        type="button"
        onClick={onToggle}
        className="h-10 text-sidebar-text hover:text-white flex items-center justify-center transition-colors"
        style={{ borderTop: `1px solid ${DIVIDER}` }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>
    </aside>
  );
}
